import { useCallback, useEffect, useRef } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';

import { OrderSchemaType } from '../order.schema';
import { useOrderCalculations } from './useOrderCalculations';
import { addDays, buildInstallments, parseConditionToOffsets } from '../../../OrderForm/utils';

interface UseSteelInstallmentsParams {
  form: UseFormReturn<OrderSchemaType>;
  isEditMode?: boolean;
  /** Condição gravada no pedido em edição: a hidratação chegar nela não regenera parcela. */
  loadedConditionId?: number;
}

export const useSteelInstallments = ({ form, isEditMode = false, loadedConditionId }: UseSteelInstallmentsParams) => {
  const { control, watch, formState: { isDirty } } = form;
  const { fields, append, remove, update, replace } = useFieldArray({ control, name: 'installments' });

  const { practicedTotal } = useOrderCalculations(form);
  const total = Number(practicedTotal) || 0;
  const orderDate = watch('orderDate');
  const paymentConditionId = watch('paymentConditionId');
  const paymentConditionName = watch('paymentConditionName');
  const installments = watch('installments') || [];

  // Em edição, parte da condição gravada: o reset da hidratação chega nela e não conta como troca.
  // Pedido salvo sem condição parte de undefined, então a primeira escolha do usuário já regenera.
  const lastConditionId = useRef<number | undefined>(isEditMode ? loadedConditionId : paymentConditionId);
  const didInit = useRef(isEditMode);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      lastConditionId.current = paymentConditionId;
      return;
    }
    if (!paymentConditionId) return;

    const conditionChanged = paymentConditionId !== lastConditionId.current;
    lastConditionId.current = paymentConditionId;

    if (isEditMode) {
      // Pedido salvo: só a troca explícita de condição regenera parcela; total sozinho não mexe.
      if (!conditionChanged) return;
    } else if (!conditionChanged && !isDirty) {
      return;
    }

    const offsets = parseConditionToOffsets(paymentConditionName);
    replace(buildInstallments(total, offsets, orderDate));
  }, [paymentConditionId, paymentConditionName, total, orderDate, isDirty, isEditMode, replace]);

  const handleAddInstallment = useCallback(() => {
    append({
      numero: fields.length + 1,
      valor: 0,
      vencimento: orderDate ?? '',
      comissaoAtiva: false,
      comissaoPercentual: undefined,
    });
  }, [append, fields.length, orderDate]);

  const handleRefreshDueDates = useCallback(() => {
    const offsets = parseConditionToOffsets(paymentConditionName);
    fields.forEach((field, index) => {
      const offset = offsets[index] ?? offsets[offsets.length - 1] ?? 0;
      update(index, {
        ...installments[index],
        vencimento: addDays(orderDate, offset),
        diasOffset: offset,
      });
    });
  }, [fields, installments, paymentConditionName, orderDate, update]);

  const sumInstallments = installments.reduce(
    (acc: number, parcela: any) => acc + (Number(parcela?.valor) || 0),
    0,
  );
  const hasMismatch = fields.length > 0 && Math.abs(sumInstallments - total) > 0.01;

  return {
    fields,
    remove,
    handleAddInstallment,
    handleRefreshDueDates,
    sumInstallments,
    total,
    hasMismatch,
  };
};
